import { useState } from 'react'
import Markdown from '../lib/markdown'
import Segmented from './Segmented'

// The one space form, for New and for Edit, as three numbered sections:
// 1 Engagement, 2 Principal, 3 Invitations. The parent owns the draft and the
// submit. Sections 1 and 2 are the same fields on both — the brief only on
// Edit, where the template (links already snapshot from it) renders
// read-only. The principal's email is editable on both: on Edit, saving a new
// one moves the space to that address and closes the old one's access. Section
// 3 differs, so
// the parent hands it in as `invitations`; this form closes it with the
// notify checkbox, which is the last thing on the form either way.
//
// `compact` is the dialog's tighter rhythm (28px markers, no meta lines).

export function FormSection({ number, title, meta = null, action = null, compact = false, children }) {
  return (
    <section className={`form-section${compact ? ' form-section--compact' : ''}`}>
      <div className="form-section__head">
        <span className="disc-num" aria-hidden="true">
          {number}
        </span>
        <div className="form-section__grow">
          <h3 className="form-section__title">{title}</h3>
          {meta && <p className="form-section__meta">{meta}</p>}
        </div>
        {action}
      </div>
      <div className="form-section__body">{children}</div>
    </section>
  )
}

export default function SpaceForm({
  mode,
  draft,
  set,
  fieldErrors = {},
  templates = [],
  templateName = null,
  variables = [], // the template's, on Edit: one default field each
  invitations = null,
  invitationsMeta = null,
  invitationsAction = null,
  disabled = false,
  compact = false,
}) {
  const [briefView, setBriefView] = useState('write')
  const editing = mode === 'edit'
  const prefix = editing ? 'space' : 'new-space'

  const inputClass = (name) => `field__input${fieldErrors[name] ? ' field__input--invalid' : ''}`

  function field(name, label, input, { note, help, full = false } = {}) {
    const id = `${prefix}-${name}`
    return (
      <div className={`field${full ? ' field--full' : ''}`}>
        <label className="field__label" htmlFor={id}>
          {label}
          {note && <span className="field__label-note"> — {note}</span>}
        </label>
        {input(id)}
        {fieldErrors[name] ? (
          <span className="field__error" role="alert">
            {fieldErrors[name]}
          </span>
        ) : (
          help && <span className="field__help">{help}</span>
        )}
      </div>
    )
  }

  function textInput(name, type = 'text', extra = {}) {
    return (id) => (
      <input
        id={id}
        type={type}
        className={inputClass(name)}
        value={draft[name] ?? ''}
        onChange={(event) => set(name, event.target.value)}
        autoComplete="off"
        disabled={disabled || extra.disabled}
        readOnly={extra.readOnly}
      />
    )
  }

  const body = compact ? '' : ' card space-card'

  return (
    <div className="space-form">
      <FormSection
        number={1}
        title="Engagement"
        meta={compact ? null : 'What the space is for and when it ends'}
        compact={compact}
      >
        <div className={`form-fields${body}`}>
          <div className="space-form__grid">
            {field('client_name', 'Client name', textInput('client_name'))}
            {field('engagement', 'Engagement', textInput('engagement'))}
            {field('ends_at', 'End date', textInput('ends_at', 'date'), {
              help: editing
                ? 'Moving it moves every live link, and the principal’s access, with it.'
                : 'Links and the principal’s access end here.',
            })}
            {editing
              ? field(
                  'template_id',
                  'Template',
                  (id) => (
                    <input
                      id={id}
                      className="field__input"
                      value={templateName ?? '—'}
                      readOnly
                      disabled
                    />
                  ),
                  { help: 'Set when the space was created.' },
                )
              : field('template_id', 'Template', (id) => (
                  <select
                    id={id}
                    className={inputClass('template_id')}
                    value={draft.template_id}
                    onChange={(event) => set('template_id', event.target.value)}
                    disabled={disabled}
                  >
                    {templates.length === 0 && <option value="">No templates available</option>}
                    {templates.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                ))}
            {field('purpose', 'Purpose', textInput('purpose'), {
              note: 'optional',
              help: 'One sentence. Available to copy as {PURPOSE}.',
              full: true,
            })}
          </div>

          {editing && variables.length > 0 && (
            <fieldset className="field field--full" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="field__label">
                Default answers
                <span className="field__label-note"> — optional</span>
              </legend>
              <span className="field__help" style={{ marginBottom: 'var(--space-xs)' }}>
                What each new staff member’s answers start as, on the roster and on the
                principal’s page. Blank means the engagement name. Untick “Show to
                principal” and she is never asked: the default is filled in for her, and the
                agent still receives it.
              </span>
              <div className="space-form__grid">
                {variables.map((variable) => {
                  const id = `${prefix}-default-${variable.key}`
                  const hidden = draft.hidden_variables ?? []
                  const shown = !hidden.includes(variable.key)
                  return (
                    <div className="field" key={variable.key}>
                      <label className="field__label" htmlFor={id}>
                        {variable.label}
                      </label>
                      <input
                        id={id}
                        type="text"
                        className="field__input"
                        value={draft.variable_defaults?.[variable.key] ?? ''}
                        placeholder={draft.engagement || ''}
                        onChange={(event) =>
                          set('variable_defaults', {
                            ...(draft.variable_defaults ?? {}),
                            [variable.key]: event.target.value,
                          })
                        }
                        autoComplete="off"
                        disabled={disabled}
                      />
                      {variable.help && <span className="field__help">{variable.help}</span>}
                      <label className="var-row__check">
                        <input
                          type="checkbox"
                          checked={shown}
                          disabled={disabled}
                          onChange={(event) =>
                            set(
                              'hidden_variables',
                              event.target.checked
                                ? hidden.filter((key) => key !== variable.key)
                                : [...hidden, variable.key],
                            )
                          }
                        />
                        Show to principal
                      </label>
                    </div>
                  )
                })}
              </div>
            </fieldset>
          )}

          {editing && (
            <div className="field">
              <div className="brief-head">
                <label className="field__label" htmlFor={`${prefix}-brief`}>
                  Brief<span className="field__label-note"> — markdown, optional (This Brief will be sent to the ElevenLabs Agent and can be referenced in 
                  participant calls, share information at your own discretion)</span>
                </label>
                <Segmented
                  label="Brief view"
                  value={briefView}
                  onChange={setBriefView}
                  options={[
                    { value: 'write', label: 'Write' },
                    { value: 'preview', label: 'Preview' },
                  ]}
                />
              </div>
              {briefView === 'write' ? (
                <textarea
                  id={`${prefix}-brief`}
                  rows={6}
                  className={`${inputClass('brief')} brief-input`}
                  placeholder="Background, goals, anything the principal should read first."
                  value={draft.brief ?? ''}
                  onChange={(event) => set('brief', event.target.value)}
                  disabled={disabled}
                />
              ) : (
                <div className="card card--alt brief-preview" id={`${prefix}-brief`} aria-live="polite">
                  {(draft.brief ?? '').trim() ? (
                    <Markdown source={draft.brief} />
                  ) : (
                    <p className="text-body-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                      Nothing to preview yet.
                    </p>
                  )}
                </div>
              )}
              {fieldErrors.brief && (
                <span className="field__error" role="alert">
                  {fieldErrors.brief}
                </span>
              )}
            </div>
          )}
        </div>
      </FormSection>

      <FormSection
        number={2}
        title="Principal"
        meta={compact ? null : 'Who owns this space on the client side'}
        compact={compact}
      >
        <div className={`form-fields${body}`}>
          <div className="space-form__grid">
            {field('principal_name', 'Name', textInput('principal_name'))}
            {field('principal_title', 'Title', textInput('principal_title'), { note: 'optional' })}
            {field(
              'principal_email',
              'Work email',
              textInput('principal_email', 'email'),
              {
                full: true,
                help: editing
                  ? 'Their personal link signs in as this address. Changing it closes the old address’s access when you save; share the space again to send the new one their link.'
                  : undefined,
              },
            )}
          </div>
        </div>
      </FormSection>

      <FormSection
        number={3}
        title={editing ? 'Invitation' : 'Invitations'}
        meta={compact ? null : invitationsMeta}
        action={invitationsAction}
        compact={compact}
      >
        <div className={`form-fields${body}`}>
          {invitations}
          <label className="check-row" htmlFor={`${prefix}-notify`}>
            <input
              id={`${prefix}-notify`}
              type="checkbox"
              checked={Boolean(draft.notify_principal)}
              onChange={(event) => set('notify_principal', event.target.checked)}
              disabled={disabled}
            />
            <span>
              <span className="check-row__label">Email new results to the principal as well.</span>
              <span className="check-row__help">
                One line per call — “Sam completed their conversation · 14 min” — from Capston,
                nothing else. Off by default; Capston still gets every receipt.
              </span>
            </span>
          </label>
        </div>
      </FormSection>
    </div>
  )
}
